<template>

  <table-tools :actions="tableActions" :headers="headers" v-model:enabled-headers="enabledHeaders" remember-headers="location-index-new2">

    <table-filter v-if="canManageOrganizations">
      <o-autocomplete
        :label="$t('titles.organization')"
        v-model.number="filters.organizationID"
        :items="Organization.getOptions()"
        clearable
      />
    </table-filter>

    <table-filter>
      <o-text-field
        :label="$t('titles.name')"
        v-model="filters.name"
        clearable
      />
    </table-filter>

    <table-filter>
      <o-text-field
        :label="$t('titles.address')"
        v-model="filters.address"
        clearable
      />
    </table-filter>

    <table-filter>
      <o-select
        :label="$t('titles.status')"
        v-model="filters.status"
        :items="statusOptions"
        clearable
      />
    </table-filter>

  </table-tools>

  <v-data-table
    ref="dataTable"
    :headers="visibleHeaders"
    :items="locations"
    :sort-by="sortBy"
    v-model:page="page"
    :items-per-page="itemsPerPage"
    :loading="loadingLocations"
  >

    <template v-slot:item.locationName="{ item }">
      {{ item.locationName }}<br>
      <span class="text-grey-darken-1">{{ item.address }}</span>
    </template>

    <template v-slot:item.status="{ value }">
      {{ $t(`enums.location_statuses.${value}`) }}
    </template>

    <template v-slot:item.gatedBarrier="{ item }">
      {{ $t(item.gatedBarrier ? 'misc.yes' : 'misc.no') }}
    </template>

    <template v-slot:item.actions="{ item }">
      <ActionGroup :actions="getActions(item.ID)" />
    </template>

    <template v-slot:bottom>
      <!-- TODO: remove the "All" option from the pagination -->
      <DataTablePagination
        :items="locations"
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        @update:page="dataTable?.$el.scrollIntoView()"
      />
    </template>

  </v-data-table>

</template>

<script setup lang="ts">

import { ref, computed, reactive, inject, watch, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import DataTablePagination from '../../DataTablePagination.vue'

// models
import Location from '../../../models/Location'
import Organization from '../../../models/Organization'
import UserSolution from '../../../models/UserSolution'

// misc
import store from '../../../plugins/vuex'
import NotificationService from '../../../services/NotificationService'
import { QuerySyncer, NumberSerializer } from '../../../utils/QuerySyncer'
import LocationSolution from '../../../models/LocationSolution'
import type { Can } from '../../../main'
import type { Model } from '@vuex-orm/core'
import type { ColumnPickerHeader, TableToolsAction } from '../../../types/misc'
import type { VSelectItem } from '../../../types/vuetify'

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.locations')

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')
const canManageOrganizations = $can('organizations.manage')
const canWriteLocations = $can('locations.write')
const canReadVlps = $can('vlps.read')
const canUsersIndex = $can('users.index')
const canViewArchivedLocations = $can('archivedlocations.read')
const generalIcon = canWriteLocations ? '$edit' : '$view'

const filters = reactive({
  organizationID: null,
  name: '',
  address: '',
  status: 'live',
})

new QuerySyncer({
  organization_id: {
    watchable: () => filters.organizationID,
    hydrate: (newValue) => filters.organizationID = newValue,
    serializer: NumberSerializer,
  },
  name: {
    watchable: () => filters.name,
    hydrate: (newValue) => filters.name = newValue,
  },
  address: {
    watchable: () => filters.address,
    hydrate: (newValue) => filters.address = newValue,
  },
  status: {
    watchable: () => filters.status,
    hydrate: (newValue) => filters.status = newValue,
  },
})

const tableActions = computed<TableToolsAction[]>(() => [
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadLocations,
    busy: loadingLocations.value,
  },
  {
    title: t('actions.create_location'),
    icon: '$create',
    to: {name: 'locationDetail', params: {locationID: 'new'}},
    hidden: !canWriteLocations,
  },
])

/**
 * Data table configs
 */

const dataTable = useTemplateRef('dataTable')

const headers: ColumnPickerHeader[] = [
  {
    title: t('titles.id'),
    key: 'ID',
    cellProps: {class: 'td-id'},
    hidden: !canSeeIds,
  },
  {
    title: t('titles.organization'),
    key: 'organization.name',
    hidden: !canManageOrganizations,
  },
  {
    title: t('titles.location'),
    key: 'locationName',
    cellProps: {class: 'td-title'},
    toggleable: false,
  },
  {
    title: 'Stalls',
    key: 'stalls',
    align: 'center',
    sortable: false,
    children: [
      {
        title: 'Total',
        key: 'totalStalls',
      },
      {
        title: 'Online',
        key: 'onlineStalls',
      },
    ],
  },
  {
    title: t('titles.gated_barrier'),
    key: 'gatedBarrier',
  },
  {
    title: t('titles.users_with_access'),
    key: 'usersWithSolutions',
  },
  {
    title: t('titles.status'),
    key: 'status',
  },
  {
    title: '',
    key: 'actions',
    sortable: false,
    cellProps: {class: 'td-actions'},
    hideFromPicker: true,
  },
]

const enabledHeaders = ref([
  'ID', 'organization.name', 'locationName', 'totalStalls', 'onlineStalls',
  'gatedBarrier', 'usersWithSolutions', 'status', 'actions']
)

const visibleHeaders = computed<ColumnPickerHeader[]>(() => {
  return headers.filter((header) => {
    return !header.hidden && enabledHeaders.value.includes(header.key as string)
  })
})

const sortBy = ref([{ key: 'locationName' }])

/**
 * Pagination
 */

const page = ref(1)
const itemsPerPage = ref(10)

// when anything that affects the main query is changed, reset the page to 1
watch(filters, () => page.value = 1)

/**
 * Location data
 */

const loadingLocations = ref<boolean>(false)

const locations = computed<Location[]>(() => {

  const query = Location.query()
    .with('organization')
    .orderBy((location: Model) => {
      return (location as Location).getDisplayName().toUpperCase()
    })

  if (filters.status) {
    query.where('status', filters.status)
  }

  if (filters.organizationID !== null) {
    query.where('organizationID', filters.organizationID)
  }

  if (filters.name !== null && filters.name.length > 0) {
    query.where((location: Location) => {
      return location.locationName.toUpperCase().indexOf(
        filters.name.trim().toUpperCase()
      ) !== -1
    })
  }

  if (filters.address && filters.address.length > 0) {
    query.where((location: Location) => {
      return location.address.toUpperCase().indexOf(
        filters.address.trim().toUpperCase()
      ) !== -1
    })
  }

  if (store.state.auth.user.role !== 'super_admin') {

    // Only display this location if the user has at least "read" access to
    // at least one user solution (#OCR-29)
    // https://trello.com/c/D3raxkhC
    query.where((location: Model) => {
      const userSolution = UserSolution.query()
        .where('userID', store.state.auth.user.ID)
        .where('locationID', (location as Location).ID)
        .first()
      if (!userSolution) {
        return false
      }
      return userSolution.universalInventoryRead ||
        userSolution.rateManagementRead ||
        userSolution.blackoutsRead ||
        userSolution.reportingRead ||
        userSolution.rateParityRead ||
        userSolution.maxCapacityRead
    })

  }

  return query.get()

})

function loadLocations() {

  loadingLocations.value = true

  Location.fetchAll()
    .then(_loadedLocations => {
      loadingLocations.value = false
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_locations'))
    })

}

function getActions(locationId: number) {

  const output = [
    { label: t('titles.general'), icon: generalIcon, to: { name: 'locationDetail', params: { locationID: locationId } } },
    { label: t('titles.solutions'), icon: '$solution', to: { name: 'locationSolutions', params: { locationID: locationId } } },
    { label: t('titles.products'), icon: '$product', to: { name: 'locationProductIndex', params: { locationID: locationId } } },
  ]

  if (canReadVlps) {
    output.push(
      { label: t('titles.venues'), icon: '$venue', to: { name: 'locationVenueIndex', params: { locationID: locationId } } }
    )
  }

  if (canUsersIndex) {
    output.push(
      { label: t('titles.users'), icon: '$users', to: { name: 'locationUsers', params: { locationID: locationId } } }
    )
  }

  output.push(
    { label: t('titles.parcs'), icon: '$parcs', to: { name: 'locationParcs', params: { locationID: locationId} } }
  )

  const userHasUriSolution = $can('locations.uri', locationId)
  const locationHasUriSolution = LocationSolution.query().where('locationID', locationId).first()?.uri

  if (userHasUriSolution && locationHasUriSolution) {
    output.push({
      label: t('titles.uri'),
      icon: '$universalRatesAndInventory',
      to: {name: 'locationUri', params: {locationID: locationId}},
    })
  }

  return output

}

/**
 * Miscellaneous
 */

const statusOptions = computed<VSelectItem[]>(() => {

  const options = [
    {value: 'onboarded', title: t('enums.location_statuses.onboarded')},
    {value: 'live', title: t('enums.location_statuses.live')},
  ]

  if (canViewArchivedLocations) {
    options.push({value: 'archived', title: t('enums.location_statuses.archived')})
  }

  // TODO

  return options

})

</script>
